import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import dayjs from 'dayjs';
import * as customParseFormat from 'dayjs/plugin/customParseFormat';

import * as actions from 'containers/App/actions';
import {
  getInitialProjects as actionGetProjects,
  getProjects as actionGetAdditionalProjects,
} from 'containers/Projects/actions';
import { fetchEvents as fetchEventsAction } from 'containers/Events/actions';
import Calendar from 'components/Calendar';
import APModal from 'components/Modal/APModal';
import { isHexColor, getContrastColor, getFadeColor } from 'utils/colors';
import { EVENT_COLORS } from 'utils/constants';
import TimezoneDifferenceModal from './components/timezone/TimezoneDifferenceModal';
import TimezoneSetModal from './components/timezone/TimezoneSetModal';

dayjs.extend(customParseFormat);

const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

// Fixes time offsets for all day events.
function applyTimeToZone(time, newTimeZone) {
  return time ? dayjs(time).tz(newTimeZone).format() : null;
};

const CalendarIndex = () => {
  const dispatch = useDispatch();
  // Raw events is an object of every event fetched, keyed by id of the event.
  const rawEvents = useSelector((state) => state.events.events);
  const isProjectsLoaded = useSelector((state) => state.projects.isProjectsLoaded);
  const activeProjects = useSelector((state) => state.projects.activeProjects);
  const userTimezone = useSelector((state) => state.global.userInfo.timezone);
  const integration = useSelector((state) => state.global.userInfo.integrations)?.find(i => i.grantStatus === 'valid');
  const { loginCalendars = [] } = integration || {};
  const [displayDateTip, setDisplayDateTip] = useState(false);
  // Create an object keyed by calendar ids for easy access the colors of each calendar.
  const loginCalendarsById = Object.fromEntries(loginCalendars.map(lc => [lc.calendarsId, lc]));

  const timeZone = userTimezone || browserTimezone;
  const {
    getProjects,
    getAddtProjects,
  } = bindActionCreators({
    ...actions,
    getProjects: actionGetProjects,
    getAddtProjects: actionGetAdditionalProjects,
  }, dispatch);

  const [projects, setProjects] = useState([]);
  const [projectsPage, setProjectsPage] = useState(0);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorModalMessage, setErrorModalMessage] = useState('');

  const isLoaded = isProjectsLoaded;

  // fetchActionsEvent returns function. This way we can pass the dispatch function inside fetch action.
  const fetchEvents = (start, end, includePermissions, loadedDateRanges) =>
    fetchEventsAction(start, end, includePermissions, loadedDateRanges);

  const refetchEvents = (currentDate) => {
    // Current date will be the calendar header title.
    const start = dayjs(currentDate, 'MMMM YYYY').startOf('month');
    const end = dayjs(currentDate, 'MMMM YYYY').endOf('month');
    fetchEvents(start.toDate(), end.toDate(), true)(dispatch);
  };

  const events = useMemo(() => {
    // Raw events is an object of events hashed by id. This will convert events to an array.
    const eventsArray = Object.values(rawEvents);
    return eventsArray.slice().map(ev => {
      const event = ev;

      // All day events need to be normalized against the current user's timezone.
      if (!ev.allDay) {
        event.start = applyTimeToZone(ev.start, timeZone);
        event.end = applyTimeToZone(ev.end, timeZone);
      }

      const isPast = ev.allDay
        ? dayjs().tz(timeZone).isAfter(dayjs.tz(event.start, timeZone), 'day')
        : dayjs().tz(timeZone).isAfter(dayjs.tz(event.end, timeZone));

      // Check if the event is a timed event (not all-day)
      // and starts and ends on the same calendar day.
      // Handles edge case where the end time is exactly at midnight (00:00) the next day.
      const isTimedSingleDay = !ev.allDay &&
        (!event.end || dayjs(event.start).isSame(dayjs(event.end).subtract(1, 'second'), 'day'));

      // Determine if the text should be white or black depending on the background color
      // of the add day event in the calendar.
      if (event.externalCalendarId && loginCalendarsById[event.externalCalendarId]) {
        const { hexColor } = loginCalendarsById[event.externalCalendarId];
        if (hexColor && isHexColor(hexColor)) {
          event.backgroundColor = hexColor;
          event.textColor = !isTimedSingleDay ? getContrastColor(hexColor): '#000000';
        }
        // #TODO: Likely should refactor this color into the constants and use this color on the api side.
        // Pulled this color from the outlook banner.
        else if (integration.provider === 'microsoft') {
          event.backgroundColor = '#0F6CBD';
          event.textColor = !isTimedSingleDay ? getContrastColor('#0F6CBD') : '#000000';
        }
      } else if (event.type && event.type !== 'checklist') {
        const hexColor = EVENT_COLORS[event.type] || EVENT_COLORS.default;
        event.backgroundColor = hexColor;
        event.textColor = getContrastColor(hexColor);
      }

      if (['wedding', 'project_date', 'external'].includes(event?.type) && isPast) {
        event.backgroundColor = getFadeColor(event.backgroundColor);
        event.textColor = getFadeColor(event.textColor);
      }

      return event;
    });
  }, [rawEvents, timeZone, loginCalendarsById]);

  /* eslint-disable-next-line no-unused-vars */
  const setErrorModal = (message) => {
    setErrorModalMessage(message);
    setErrorModalOpen(true);
  };

  const errorModalCloseHandler = () => {
    setErrorModalOpen(false);
    setErrorModalMessage(null);
  };

  // #TODO: This might be better fetching either next month or previous month,
  // depending on which direction the calendar moved. This could blow up the
  // api requests to nylas also, so might want to separate that out.
  const handleDateChange = (info) => {
    const start = dayjs(info.startStr).subtract(1).startOf('month');
    const end = dayjs(info.endStr).add(2).endOf('month');
    fetchEvents(start.toDate(), end.toDate())(dispatch);
  };

  // Initial events fetch. Fetch two months ahead and one month behind to help
  // with changing months.
  useEffect(() => {
    const start = dayjs().subtract(1, 'month').startOf('month');
    const end = dayjs().add(2, 'month').endOf('month');
    fetchEvents(start.toDate(), end.toDate(), true)(dispatch);
  }, []);

  // Need projects to supply data for certain cards, e.g. project task cards.
  useEffect(() => {
    if (projectsPage === 0) {
      getProjects({ page: projectsPage, pageSize: 24, key: '' });
    } else {
      getAddtProjects({ page: projectsPage, pageSize: 24, key: '' });
    }
  }, [projectsPage]);

  // #TODO: We aren't doing this with leads because the number of leads can be
  // massive compared to projects. Would need to refactor how we store leads in
  // the redux store so we could fetch single leads and add them to the state,
  // while maintaining sort/filters of the lead index.
  // Fetch additional projects, if the first call denotes additional projects.
  useEffect(() => {
    if (activeProjects.hasMore) {
      setProjectsPage(prev => prev + 1);
    }

    if (activeProjects.projects.length && !activeProjects.hasMore) {
      const projectList = activeProjects.projects.map(item => ({
        id: item.weddingId,
        name: item.weddingName,
        weddingDate: item.weddingDate,
        allDayEvent: item.allDayEvent,
        startTime: item.startTime,
        endTime: item.endTime,
        timezone: item.timezone,
        serviceType: item.serviceType,
        weddingAddress: item.weddingAddress,
        weddingLocation: item.weddingLocation,
        weddingGuestCount: item.weddingGuestCount,
        weddingPartners: item.weddingPartners,
        image306: item.image306,
      }));

      setProjects(projectList);
    }
  }, [activeProjects.projects.length]);

  if (!isLoaded) {
    return null;
  }

  return (
    <div
      className="calendar-page"
    >
      <Calendar
        projects={projects}
        events={events}
        timeZone={timeZone}
        onDateChange={handleDateChange}
        refetchEvents={refetchEvents}
        integration={integration}
        loginCalendarsById={loginCalendarsById}
        displayDateTip={displayDateTip}
        setDisplayDateTip={setDisplayDateTip}
      />

      <APModal
        open={errorModalOpen}
        onClose={errorModalCloseHandler}
        actions={(
          <button
            onClick={errorModalCloseHandler}
            className="button"
          >
            Ok
          </button>
        )}
      >
        <div>{errorModalMessage}</div>
      </APModal>
      <TimezoneDifferenceModal />
      <TimezoneSetModal />
    </div>
  );
};

export default CalendarIndex;
